/**
 * M2 data contract (plan §2.5, §2.9): the id lists, the Zod schemas of the ten M2 JSON files and their runtime
 * shapes. Degrees in JSON, radians at runtime, as in M1. `src/data/m2.ts` parses the bundled files once
 * (`loadM2Data`) and cross-validates them against `GameData` (ship, weapon, AI profile and wave references).
 *
 * Every number in the JSON files is a starting value tuned in playtest (spec §9.3 pacing note). The runtime
 * objects are plain and mutable so the dev panel (M2-23) can tune them live; `m2DataToRaw` (m2.ts) turns them
 * back into the file shapes for "Copy JSON".
 *
 * FROZEN after wave 1 (plan §1.2): later tasks may add optional members only.
 */
import { z } from 'zod'
import { PATTERN_IDS, type PatternId } from '../career/types'
import type { RegionId } from '../galaxy/types'
import { FACTION_IDS, type FactionId } from '../render/contracts'
import { ASSET_ID, SOCKET_NAME } from './assetSchema'
import patternsJson from './patterns.json'
import { shipBandSchema, WEAPON_IDS, type WeaponId } from './schemas'

// ---------------------------------------------------------------------------------------------
// Ids (plan §2.5)
// ---------------------------------------------------------------------------------------------

/** The raw ware tier of simulation spec §3.1, in canonical order (a ware index is a position in this list). */
export const WARE_IDS = ['ferrite', 'silicates', 'ice', 'titanium', 'platinum', 'tetherstone', 'refractory_ore'] as const
export type WareId = (typeof WARE_IDS)[number]
export const WARE_COUNT = WARE_IDS.length
/** Index of tetherstone in `WARE_IDS` (D-031: fracture rocks only, Refinery-only buyer). */
export const TETHERSTONE_INDEX: number = WARE_IDS.indexOf('tetherstone')

/** Salvage component types in canonical order (`SEV.partSecured.a` is a position in this list). */
export const COMPONENT_TYPES = ['reactor', 'radiator', 'weapon', 'shieldGen', 'thruster', 'cargoPod', 'dataCore'] as const
export type ComponentType = (typeof COMPONENT_TYPES)[number]

/**
 * Component types that carry a Mk (economy plan SY-4a): the ones a module slot can take (reactor → power, weapon →
 * pdc or laser, shieldGen → shield, thruster → thrusters). The rest (radiator, cargoPod, dataCore) are Mk 0.
 */
export const MK_COMPONENT_TYPES = ['reactor', 'weapon', 'shieldGen', 'thruster'] as const satisfies readonly ComponentType[]

/** The galaxy regions salvage tiers are tabled by (`salvage.json tiers`), gate to rim. */
export const SALVAGE_REGIONS = ['gate', 'mid', 'deep', 'rim'] as const satisfies readonly RegionId[]

/**
 * Factions for standing (factions.md data ids; M4/M5 reuse them). Not the render `FactionId`, which is a
 * livery: stations pick theirs through `stations.json look`.
 */
export const SIM_FACTION_IDS = ['authority', 'tethys', 'meridian', 'varro_security', 'mutual', 'holdfast', 'raider'] as const
export type SimFactionId = (typeof SIM_FACTION_IDS)[number]

/** Fitting slots. `weapon1` holds the PDC family (M1 primary slot 0), `weapon2` the pulse-laser family (slot 1). */
export const SLOT_IDS = ['weapon1', 'weapon2', 'miningLaser', 'salvageBeam', 'cargo', 'shield', 'power', 'thrusters', 'scanner'] as const
export type SlotId = (typeof SLOT_IDS)[number]

export const MODULE_KINDS = ['pdc', 'laser', 'mining', 'salvage', 'cargo', 'shield', 'power', 'thrusters', 'scanner'] as const
export type ModuleKind = (typeof MODULE_KINDS)[number]
export const MODULE_MKS = [1, 2, 3] as const
export type ModuleMk = (typeof MODULE_MKS)[number]
/** A base module: `<kind>.mk<1-3>`, e.g. `mining.mk2` (the 27 entries of `modules.json`). */
export type BaseModuleId = `${ModuleKind}.mk${ModuleMk}`
/** The rungs a faction pattern covers (M8 MC-f1: Mk II and Mk III only). */
export type PatternMk = Exclude<ModuleMk, 1>
/** A pattern module (M8 MC-f2a): a Mk II/III base id plus a pattern id (`patterns.json` key), e.g. `pdc.mk2.varro`. */
export type PatternModuleId = `${ModuleKind}.mk${PatternMk}.${PatternId}`
/**
 * Any module a ship can fit or a fab order can make: a base id or a pattern id. Only base ids are in `MODULE_IDS`
 * and `ModulesData.byId`; `data/m2.ts resolveModule` resolves both (MC-f2a).
 */
export type ModuleId = BaseModuleId | PatternModuleId

/** Station ids are data (`stations.json`: `hub`, `refinery`, `scrapyard`); M3 adds more without renaming these. */
export type StationId = string

export const STATION_SERVICES = ['market', 'outfitting', 'repair', 'lease', 'contracts'] as const
export type StationService = (typeof STATION_SERVICES)[number]

export const WARE_RARITIES = ['common', 'uncommon', 'rare', 'veryRare'] as const
export type WareRarity = (typeof WARE_RARITIES)[number]

/**
 * The M3 bridge's contract types (M3 plan §7 item 4, slice R22g), appended after M2's four: repair a lane ring,
 * deliver relay parts to an offline relay's port, chart a system. Only issued in the Reach (`src/contracts/reach.ts`).
 */
export const REACH_CONTRACT_TYPES = ['laneRepair', 'relayParts', 'chartSystem'] as const
export type ReachContractType = (typeof REACH_CONTRACT_TYPES)[number]

export const CONTRACT_TYPES = ['delivery', 'recovery', 'clearRaiders', 'courier', ...REACH_CONTRACT_TYPES] as const
export type ContractType = (typeof CONTRACT_TYPES)[number]

/** Scanner accuracy per Mk (spec §5.2): Mk I value ranges, Mk II ±10%, Mk III exact. */
export const SCANNER_ACCURACIES = ['range', 'pm10', 'exact'] as const
export type ScannerAccuracy = (typeof SCANNER_ACCURACIES)[number]

/** Most salvageable parts one wreck (or one derelict section) carries; sizes the scan readout. */
export const MAX_PARTS_PER_WRECK = 16
/** Most cut anchors one part carries (the component `anchors` range tops out here). */
export const MAX_ANCHORS_PER_PART = 4

/** The 27 base module ids, kind-major. Pattern ids are not listed (they resolve through `resolveModule`). */
export const MODULE_IDS: readonly BaseModuleId[] = MODULE_KINDS.flatMap((kind) => MODULE_MKS.map((mk) => moduleId(kind, mk)))

/** `<kind>.mk<1-3>` with an optional `.<pattern>`; `$` anchors it, so `varro_security` never reads as `varro`. */
const MODULE_ID_RE = new RegExp(`^(${MODULE_KINDS.join('|')})\\.mk([123])(?:\\.(${PATTERN_IDS.join('|')}))?$`)

/**
 * The module kinds each pattern covers, from `patterns.json` (`data/patterns.ts` validates that file; it is read
 * raw here because that schema imports this module). A pattern absent from the file covers nothing.
 */
const PATTERN_KINDS: Readonly<Record<PatternId, readonly string[]>> = (() => {
  const file: Partial<Record<PatternId, { modules: readonly string[] }>> = patternsJson.patterns
  const out = {} as Record<PatternId, readonly string[]>
  for (const p of PATTERN_IDS) out[p] = file[p]?.modules ?? []
  return out
})()

export function moduleId(kind: ModuleKind, mk: ModuleMk): BaseModuleId {
  return `${kind}.mk${mk}`
}

/** `<kind>.mk<mk>.<pattern>` (M8 MC-f2a). Not checked against the pattern's coverage; `isModuleId` checks that. */
export function patternModuleId(kind: ModuleKind, mk: PatternMk, pattern: PatternId): PatternModuleId {
  return `${kind}.mk${mk}.${pattern}`
}

/**
 * A base id, or a pattern id whose pattern covers that kind, on Mk II/III only (M8 MC-f2a). Mk I patterns, unknown
 * patterns, uncovered kinds and faction ids in the pattern place (`pdc.mk2.varro_security`) are refused.
 */
export function isModuleId(value: unknown): value is ModuleId {
  if (typeof value !== 'string') return false
  const m = MODULE_ID_RE.exec(value)
  if (!m) return false
  const pattern = m[3] as PatternId | undefined
  return pattern === undefined || (m[2] !== '1' && PATTERN_KINDS[pattern].includes(m[1]))
}

/** A base module id (no pattern): what `modules.json` lists and `ModulesData.byId` holds. */
export function isBaseModuleId(value: unknown): value is BaseModuleId {
  return isModuleId(value) && value.split('.').length === 2
}

/** Splits a module id into its kind and Mk, plus its `pattern` for a pattern id (a base id has no `pattern` key). */
export function parseModuleId(id: ModuleId): { kind: ModuleKind; mk: ModuleMk; pattern?: PatternId } {
  const m = isModuleId(id) ? MODULE_ID_RE.exec(id) : null
  if (!m) throw new Error(`not a module id: "${id}"`)
  const kind = m[1] as ModuleKind
  const mk = Number(m[2]) as ModuleMk
  return m[3] === undefined ? { kind, mk } : { kind, mk, pattern: m[3] as PatternId }
}

/** The base module an id builds on: itself for a base id, `<kind>.mk<mk>` for a pattern id. */
export function baseModuleId(id: ModuleId): BaseModuleId {
  const { kind, mk } = parseModuleId(id)
  return moduleId(kind, mk)
}

/** Position of a ware in `WARE_IDS`. */
export function wareIndex(id: WareId): number {
  return WARE_IDS.indexOf(id)
}

/** Position of a component type in `COMPONENT_TYPES`. */
export function componentTypeIndex(type: ComponentType): number {
  return COMPONENT_TYPES.indexOf(type)
}

// ---------------------------------------------------------------------------------------------
// Shared schema pieces
// ---------------------------------------------------------------------------------------------

const positive = z.number().positive()
const nonNegative = z.number().nonnegative()
const unit = z.number().min(0).max(1)
const signedUnit = z.number().min(-1).max(1)
const int = z.number().int()
const name = z.string().min(1)
const slug = z.string().regex(/^[a-z][a-z0-9_]*$/)
const standing = z.number().min(-100).max(100)
const hexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/)
const vec3Tuple = z.tuple([z.number(), z.number(), z.number()])
const hpSocket = z.string().regex(/^HP_[A-Z0-9]+(?:_[A-Z0-9]+)*$/)
const assetId = z.string().regex(ASSET_ID)

/** [min, max] with min ≤ max. */
function minMax(item: z.ZodNumber): z.ZodType<[number, number]> {
  return z.tuple([item, item]).refine(([a, b]) => a <= b, { message: 'expected [min, max] with min ≤ max' })
}

/** [value at 0, value at 1], lerped by a parameter (resistance, skill); either order. */
function lerpPair(item: z.ZodNumber): z.ZodType<[number, number]> {
  return z.tuple([item, item])
}

function oneOf<T extends string>(values: readonly T[], what: string): z.ZodType<T> {
  return z.custom<T>((v) => typeof v === 'string' && (values as readonly string[]).includes(v), {
    message: `expected a ${what}: ${values.join(', ')}`,
  })
}

export const wareIdSchema = z.enum(WARE_IDS)
export const componentTypeSchema = z.enum(COMPONENT_TYPES)
export const simFactionIdSchema = z.enum(SIM_FACTION_IDS)
export const slotIdSchema = z.enum(SLOT_IDS)
export const moduleKindSchema = z.enum(MODULE_KINDS)
export const contractTypeSchema = z.enum(CONTRACT_TYPES)
export const moduleIdSchema: z.ZodType<ModuleId> = z.custom<ModuleId>(isModuleId, {
  message: `expected a module id <kind>.mk<1-3>, or <kind>.mk<2-3>.<pattern> for a pattern that covers the kind, with kind one of ${MODULE_KINDS.join(', ')}`,
})
/** A base module id only (`modules.json` entries; M8 MC-f2a). */
export const baseModuleIdSchema: z.ZodType<BaseModuleId> = z.custom<BaseModuleId>(isBaseModuleId, {
  message: `expected a module id <kind>.mk<1-3> (a base module, no pattern) with kind one of ${MODULE_KINDS.join(', ')}`,
})
/** A render livery (`FactionId`), for station looks. */
const lookSchema = oneOf<FactionId>(FACTION_IDS, 'render FactionId')

function uniqueIssues(ids: readonly string[], ctx: z.RefinementCtx, path: (string | number)[], what: string): void {
  const seen = new Set<string>()
  ids.forEach((id, i) => {
    if (seen.has(id)) ctx.addIssue({ code: 'custom', path: [...path, i], message: `duplicate ${what} "${id}"` })
    seen.add(id)
  })
}

// ---------------------------------------------------------------------------------------------
// wares.json: wares, salvage component types and the market block (spec §5.1, §6.2, §9.1)
// ---------------------------------------------------------------------------------------------

export const wareDefRawSchema = z.strictObject({
  id: wareIdSchema,
  name,
  /** What scanner Mk I-II calls it (tetherstone only: "Unidentified crystalline"). */
  unidentifiedName: name.optional(),
  /** Credits per tonne before station multipliers, drift, saturation and standing. */
  basePrice: positive,
  rarity: z.enum(WARE_RARITIES),
  /** Chunk and icon tint. */
  tint: hexColor,
})

export const componentDefRawSchema = z.strictObject({
  type: componentTypeSchema,
  name,
  /** Value = baseValue × (0.3 + 0.7 × condition); a cargo pod is worth its contents (0 here). */
  baseValue: nonNegative,
  /** Tonnes, [min, max] within 0.5-8. */
  massT: minMax(z.number().min(0.5).max(8)),
  /** Cut anchors, [min, max] within 2-4 (a cargo pod has exactly 1). */
  anchors: minMax(int.min(1).max(MAX_ANCHORS_PER_PART)),
  /** A reactor's last anchor is a coolant anchor: cutting it vents (spec §6.3). */
  hazard: z.literal('coolant').optional(),
  /** Loose-component prop model (plan §2.5 asset ids). */
  propAsset: assetId,
})

export const marketConfigSchema = z.strictObject({
  /** Buy price = sell price × this (spec §9.1). */
  buyMarkup: z.number().min(1),
  /** Standing ±100 moves prices by ± this fraction (linear). */
  standingSwing: z.number().min(0).max(0.5),
  /** satMult = 1 / (1 + k·s); s rises by qty / capacity per sale and decays with this half-life (game seconds). */
  saturation: z.strictObject({ k: positive, halfLifeSeconds: positive }),
  /** A seeded sum of two slow sines per station and item, clamped to ± maxFraction. */
  drift: z.strictObject({ maxFraction: z.number().min(0).max(0.5), periodSeconds: minMax(positive) }),
})

export const waresFileSchema = z
  .strictObject({
    /** In `WARE_IDS` order. */
    wares: z.array(wareDefRawSchema),
    /** In `COMPONENT_TYPES` order. */
    components: z.array(componentDefRawSchema),
    market: marketConfigSchema,
  })
  .superRefine((f, ctx) => {
    if (f.wares.length !== WARE_COUNT || f.wares.some((w, i) => w.id !== WARE_IDS[i])) {
      ctx.addIssue({ code: 'custom', path: ['wares'], message: `wares must list ${WARE_IDS.join(', ')} once each, in that order` })
    }
    if (f.components.length !== COMPONENT_TYPES.length || f.components.some((c, i) => c.type !== COMPONENT_TYPES[i])) {
      ctx.addIssue({
        code: 'custom',
        path: ['components'],
        message: `components must list ${COMPONENT_TYPES.join(', ')} once each, in that order`,
      })
    }
    f.wares.forEach((w, i) => {
      if (w.id === 'tetherstone' && w.unidentifiedName === undefined) {
        ctx.addIssue({ code: 'custom', path: ['wares', i, 'unidentifiedName'], message: 'tetherstone needs an unidentifiedName (scanner Mk I-II label)' })
      }
    })
    f.components.forEach((c, i) => {
      const [lo, hi] = c.anchors
      if (c.type === 'cargoPod' ? lo !== 1 || hi !== 1 : lo < 2) {
        ctx.addIssue({
          code: 'custom',
          path: ['components', i, 'anchors'],
          message: c.type === 'cargoPod' ? 'a cargo pod has exactly 1 anchor' : 'components carry 2-4 anchors',
        })
      }
    })
  })

export type WareDefRaw = z.infer<typeof wareDefRawSchema>
export type ComponentDefRaw = z.infer<typeof componentDefRawSchema>
export type MarketConfig = z.infer<typeof marketConfigSchema>
export type WaresFileRaw = z.infer<typeof waresFileSchema>

export interface WareDef extends WareDefRaw {
  /** Position in `WARE_IDS`. */
  index: number
  /** `tint` as 0xRRGGBB. */
  tintHex: number
}

export interface ComponentDef extends ComponentDefRaw {
  /** Position in `COMPONENT_TYPES`. */
  index: number
}

export interface WaresData {
  /** Indexed by ware index (`WARE_IDS` order). */
  wares: WareDef[]
  /** Indexed by component type index (`COMPONENT_TYPES` order). */
  components: ComponentDef[]
  market: MarketConfig
}

// ---------------------------------------------------------------------------------------------
// M7 S33 story seeds (spec §12.4): text only, so the naming pass is a data edit
// ---------------------------------------------------------------------------------------------

/** A named person at a counter (C8): "Ines Duarte", "Registry clerk". */
export const seedPersonSchema = z.strictObject({ name, role: name })
/** A line someone says or signs: the speaker ("Meridian contracts desk") and the words. */
export const seedLineSchema = z.strictObject({ speaker: name, text: name })
/** A decal-atlas mark id (`assets-src/decal-layout.json`, kind `mark`): the texture seed the asset pipeline paints. */
export const decalMarkSchema = z.string().regex(/^mark_[a-z0-9_]+$/)

/** A station's desk seeds (all optional): named staff, a board line, receipts, the raiders thank-you, marks. */
export const stationDeskSchema = z.strictObject({
  /** The Lease Office clerk (C8: Duarte), named on the Lease Office tab and the station header. */
  leaseClerk: seedPersonSchema.optional(),
  /** The claim desk officer (C8: Hale), named on the header; his stamp goes on claim registrations. */
  claimOfficer: seedPersonSchema.optional(),
  /** The stamp on a claim registration filed here (C8: Hale's stamp). */
  claimStamp: name.optional(),
  /** One line pinned on this station's contract board (spec §8.2: the Meridian contracts desk). */
  boardLine: seedLineSchema.optional(),
  /** The thank-you after a clear-raiders job is handed in here (C8: Hale). */
  raidersThanks: seedLineSchema.optional(),
  /** Market sale receipts (PC-8: Voss's CEO signature on Refinery receipts). */
  receipt: z.strictObject({ issuer: name, signedBy: seedPersonSchema }).optional(),
  /** Marks on the station's props (texture seed; FR stations: the first Charter mark and the unnotched hexagon). */
  marks: z.array(decalMarkSchema).optional(),
  /** What a pilot reads on the bay plate (the marks in words), shown in the station header. */
  plate: name.optional(),
})

export type StationDesk = z.infer<typeof stationDeskSchema>

// ---------------------------------------------------------------------------------------------
// stations.json: the home system's stations (spec §7.1)
// ---------------------------------------------------------------------------------------------

export const stationPricesSchema = z.strictObject({
  /** Sell-price multiplier per ware; a missing ware = the station refuses it. */
  wares: z.partialRecord(wareIdSchema, positive),
  /** Multiplier on component values; null = the station buys no components. */
  components: positive.nullable(),
  /** Multiplier on data-core values; null = the station buys no data cores. */
  dataCores: positive.nullable(),
})

export const stationDefRawSchema = z.strictObject({
  id: slug,
  name,
  /** Standing faction (not the livery). */
  faction: simFactionIdSchema,
  /** Render livery until faction materials exist (D-TBD): neutral, player_lease (Tethys yellow), first_rush. */
  look: lookSchema,
  /** Single-GLB station model (plan §2.10). */
  assetId,
  /** The bay socket on the model: its centre is the bay-box centre, local −Z points out through the mouth. */
  baySocket: z
    .string()
    .regex(SOCKET_NAME)
    .refine((s) => s.startsWith('DOCK_'), { message: 'the bay socket must be a DOCK_ socket' }),
  /** Station origin, float64 world metres. */
  position: vec3Tuple,
  /** The station turns so the bay's approach axis (socket −Z) points at this world point. */
  faceToward: vec3Tuple,
  /** Roll about the approach axis, degrees. */
  rollDeg: z.number(),
  services: z.array(z.enum(STATION_SERVICES)).min(1),
  /** Module kinds sold (every Mk of each). */
  outfitting: z.array(moduleKindSchema),
  prices: stationPricesSchema,
  /** Wares the player can buy here (each must be priced in `prices.wares`). */
  sells: z.array(wareIdSchema),
  /** Saturation capacity in tonnes, the same for every item traded here (spec §9.1: s += qty / capacity). */
  capacityT: positive.default(200),
  /** Seed of the station controller's comms voice (audio lane). */
  commsSeed: int.min(0),
  /** M7 S33 story seeds at this station's desks (spec §12.4); absent = none. */
  desk: stationDeskSchema.optional(),
})

export const stationsFileSchema = z
  .strictObject({
    /** The system these stations belong to (M2: `home`; M3 adds systems without renaming it). */
    systemId: slug,
    stations: z.array(stationDefRawSchema).min(1),
  })
  .superRefine((f, ctx) => {
    uniqueIssues(
      f.stations.map((s) => s.id),
      ctx,
      ['stations'],
      'station id',
    )
    let lease = 0
    f.stations.forEach((s, i) => {
      const at = ['stations', i]
      uniqueIssues(s.services, ctx, [...at, 'services'], 'service')
      uniqueIssues(s.outfitting, ctx, [...at, 'outfitting'], 'module kind')
      uniqueIssues(s.sells, ctx, [...at, 'sells'], 'ware')
      if (s.services.includes('lease')) lease++
      const outfits = s.services.includes('outfitting')
      if (outfits !== s.outfitting.length > 0) {
        ctx.addIssue({ code: 'custom', path: [...at, 'outfitting'], message: 'a station lists module kinds exactly when it offers the outfitting service' })
      }
      if (s.position[0] === s.faceToward[0] && s.position[1] === s.faceToward[1] && s.position[2] === s.faceToward[2]) {
        ctx.addIssue({ code: 'custom', path: [...at, 'faceToward'], message: 'faceToward equals position' })
      }
      s.sells.forEach((w, j) => {
        if (s.prices.wares[w] === undefined) {
          ctx.addIssue({ code: 'custom', path: [...at, 'sells', j], message: `sells "${w}" but has no price for it` })
        }
        if (w === 'tetherstone') ctx.addIssue({ code: 'custom', path: [...at, 'sells', j], message: 'no station sells tetherstone' })
      })
      if (s.prices.wares.tetherstone !== undefined && s.faction !== 'tethys') {
        ctx.addIssue({
          code: 'custom',
          path: [...at, 'prices', 'wares', 'tetherstone'],
          message: 'only the mining megacorp (tethys, the Refinery) buys tetherstone (first-rush.md §2)',
        })
      }
      const trades = Object.keys(s.prices.wares).length > 0 || s.prices.components !== null || s.prices.dataCores !== null
      if (s.services.includes('market') && !trades) {
        ctx.addIssue({ code: 'custom', path: [...at, 'prices'], message: 'a market station must buy something' })
      }
    })
    if (lease === 0) ctx.addIssue({ code: 'custom', path: ['stations'], message: 'one station must offer the lease service (the Lease Office)' })
  })

export type StationPrices = z.infer<typeof stationPricesSchema>
export type StationDefRaw = z.infer<typeof stationDefRawSchema>
export type StationsFileRaw = z.infer<typeof stationsFileSchema>

/** Runtime station definition: `roll` in radians. */
export interface StationDef extends Omit<StationDefRaw, 'rollDeg'> {
  /** Position in `StationsData.stations` (the runtime station index). */
  index: number
  /** Radians. */
  roll: number
}

export interface StationsData {
  systemId: string
  stations: StationDef[]
}

// ---------------------------------------------------------------------------------------------
// modules.json: slots, modules and their stats (spec §8)
// ---------------------------------------------------------------------------------------------

/** Stats per module kind (spec §8). */
export const MODULE_STATS_SCHEMAS = {
  pdc: z.strictObject({ damageMult: positive }),
  laser: z.strictObject({ damageMult: positive }),
  mining: z.strictObject({
    /** Max laser power, 0..1 (70/85/100%). */
    maxPower: z.number().gt(0).max(1),
    heatCapacityMult: positive,
    chipRateMult: positive,
    /** Highest fracture resistance tier the laser can crack (0-based; Mk I cannot crack the top tier). */
    maxTier: int.min(0),
  }),
  salvage: z.strictObject({
    /** Multiplies anchor cut time. */
    cutMult: positive,
    /** Condition lost per second of off-anchor contact (0.02 = 2%/s). */
    spillPerSec: nonNegative,
    /** Metres. */
    tractorRange: positive,
  }),
  cargo: z.strictObject({ capacityT: positive, maxPartMassT: positive }),
  shield: z.strictObject({ shieldMult: positive, regenMult: positive }),
  power: z.strictObject({ budget: positive }),
  thrusters: z.strictObject({ thrustMult: positive, maxSpeedAdd: nonNegative }),
  scanner: z.strictObject({ range: positive, accuracy: z.enum(SCANNER_ACCURACIES) }),
} as const satisfies Record<ModuleKind, z.ZodType>

export type ModuleStatsByKind = { [K in ModuleKind]: z.infer<(typeof MODULE_STATS_SCHEMAS)[K]> }

/**
 * Where a recipe can be fabricated (M8 plan MC-c1a; MC-c6's `offeredAt` reads it): `any` = any station that
 * outfits the module kind; `fabworks` = only in a system with a fabworks (a player-owned one too) or a faction yard.
 */
export const FAB_WHERE = ['any', 'fabworks'] as const
export type FabWhere = (typeof FAB_WHERE)[number]

/** The lowest condition band a salvaged component may be in to stand in for a recipe item (never Wrecked). */
export const FAB_MIN_BANDS = ['serviceable', 'intact'] as const
export type FabMinBand = (typeof FAB_MIN_BANDS)[number]

/**
 * A recipe item id: a ware (M2 raw or strategic), a good or a component type. The exact lists live on the career
 * side (`career/parts.ts GOOD_IDS`), so `craft/recipes.ts recipeFor` checks every id against them (a data test,
 * as `salvage.json recondition` is checked by `career/recondition.ts`).
 */
const recipeItemId = z.string().regex(/^[a-z][a-zA-Z0-9_]*$/)
const TETHERSTONE: string = WARE_IDS[TETHERSTONE_INDEX]

/**
 * A Mk II/III module's materials (M8 plan MC-c1a, D-TBD T23): fabricating pays `creditShare` × the module price in
 * credits plus the `inputs` (tonnes per ware or good; goods are 1 t a unit). `carry` is the one item you can't simply
 * buy where you fabricate (plan decision 3): a ware or good carry is one of the `inputs` keys (its tonnes are that
 * entry); a component carry (a salvage-only part such as a data core) is one unit and is not in `inputs`.
 * `salvageAlt`: a salvaged `component` at `minBand` or better replaces the `replaces` item (an input key, or the
 * carry: that is how a Mk II line gets a route without mining). Tetherstone is refused everywhere (D-031).
 */
export const fabMaterialsSchema = z
  .strictObject({
    creditShare: z.number().gt(0).lt(1),
    inputs: z.record(recipeItemId, positive),
    carry: recipeItemId,
    salvageAlt: z.strictObject({ component: componentTypeSchema, replaces: recipeItemId, minBand: z.enum(FAB_MIN_BANDS) }).optional(),
    where: z.enum(FAB_WHERE),
  })
  .superRefine((m, ctx) => {
    const components: readonly string[] = COMPONENT_TYPES
    const inputs = Object.keys(m.inputs)
    for (const id of inputs) {
      if (id === TETHERSTONE) ctx.addIssue({ code: 'custom', path: ['inputs', id], message: 'tetherstone is in no recipe' })
      if (components.includes(id)) ctx.addIssue({ code: 'custom', path: ['inputs', id], message: `a component (${id}) is the carry or a salvageAlt, never an input` })
    }
    if (m.carry === TETHERSTONE) ctx.addIssue({ code: 'custom', path: ['carry'], message: 'tetherstone is in no recipe' })
    else if (!components.includes(m.carry) && !inputs.includes(m.carry)) {
      ctx.addIssue({ code: 'custom', path: ['carry'], message: `the carry "${m.carry}" is a ware or good, so it must be one of the inputs (its tonnes)` })
    }
    const alt = m.salvageAlt
    if (alt === undefined) return
    if (alt.replaces === TETHERSTONE) ctx.addIssue({ code: 'custom', path: ['salvageAlt', 'replaces'], message: 'tetherstone is in no recipe' })
    else if (alt.replaces !== m.carry && !inputs.includes(alt.replaces)) {
      ctx.addIssue({ code: 'custom', path: ['salvageAlt', 'replaces'], message: `"${alt.replaces}" is neither an input nor the carry` })
    }
  })

export type FabMaterialsRaw = z.infer<typeof fabMaterialsSchema>

export const moduleRawSchema = z.strictObject({
  id: baseModuleIdSchema,
  name,
  price: positive,
  /** Power draw (power plants draw 0). */
  power: nonNegative,
  /** Validated per kind against `MODULE_STATS_SCHEMAS`. */
  stats: z.record(z.string(), z.union([z.number(), z.string()])),
  /** Mk II/III only: what fabricating it takes (M8 MC-c1a; absent = it can't be fabricated). */
  materials: fabMaterialsSchema.optional(),
})

/** File-level fabrication settings (M8 MC-c1a): the shortage card lists at most `maxListed` sources (MC-c4). */
export const modulesMaterialsSchema = z.strictObject({ shortage: z.strictObject({ maxListed: int.min(1) }) })
export type ModulesMaterials = z.infer<typeof modulesMaterialsSchema>

const utilityModuleSchema = <T extends z.ZodType>(stats: T) =>
  z.strictObject({
    name,
    price: positive,
    /** Power draw. */
    power: nonNegative,
    stats,
  })

/**
 * M6 utility modules (M6 plan P19, spec §4-§5): one-off fittings outside the Mk I-III slot system, owned per ship
 * (the fleet hangar's flags). escapePod: the captain's survival bonus is captains.json `survival.escapePod`.
 * heavyTractor: the salvager's tow mount; it tows parts up to maxPartMassT tonnes (fleet.json `towing.maxPartT`).
 * resonanceMeter (M7 S37): the Mission 2 reward, never sold; fitted on the flown ship (`ShipState.resonanceMeter`) and
 * carried by its hangar entry when the player switches ships. The resonance HUD reads only with it.
 */
export const utilityModulesSchema = z.strictObject({
  escapePod: utilityModuleSchema(z.strictObject({})),
  heavyTractor: utilityModuleSchema(z.strictObject({ maxPartMassT: positive })),
  resonanceMeter: utilityModuleSchema(z.strictObject({})),
})

export type UtilityModules = z.infer<typeof utilityModulesSchema>
export type UtilityModuleId = keyof UtilityModules

export const modulesFileSchema = z
  .strictObject({
    /** Module kinds each slot accepts. */
    slots: z.record(slotIdSchema, z.array(moduleKindSchema).min(1)),
    /** Selling a module returns this share of its price. */
    sellFraction: z.number().gt(0).max(1),
    /** Scrapping a fabricated module returns this share of its recipe's materials (M8 MC-c7 sets it). */
    scrapFraction: z.number().gt(0).max(1).optional(),
    /** Fabrication settings (M8 MC-c1a; the per-module recipes are each module's `materials`). */
    materials: modulesMaterialsSchema.optional(),
    /** The M1 weapon each weapon-module kind upgrades (derived weapon defs, D-TBD). */
    weapons: z.strictObject({ pdc: z.enum(WEAPON_IDS), laser: z.enum(WEAPON_IDS) }),
    modules: z.array(moduleRawSchema),
    /** M6 utility modules (optional: the M2 contract is frozen to additive members). */
    utility: utilityModulesSchema.optional(),
  })
  .superRefine((f, ctx) => {
    const seen = new Map<string, number>()
    f.modules.forEach((m, i) => {
      if (seen.has(m.id)) ctx.addIssue({ code: 'custom', path: ['modules', i, 'id'], message: `module ${m.id} appears more than once` })
      seen.set(m.id, i)
      if (!isModuleId(m.id)) return
      const { kind, mk } = parseModuleId(m.id)
      const stats = MODULE_STATS_SCHEMAS[kind].safeParse(m.stats)
      if (!stats.success) {
        for (const issue of stats.error.issues) {
          ctx.addIssue({ code: 'custom', path: ['modules', i, 'stats', ...issue.path.map(String)], message: `${kind} stats: ${issue.message}` })
        }
      }
      if (kind === 'power' && m.power !== 0) ctx.addIssue({ code: 'custom', path: ['modules', i, 'power'], message: 'power plants draw no power' })
      if (m.materials !== undefined && mk === 1) {
        ctx.addIssue({ code: 'custom', path: ['modules', i, 'materials'], message: `${m.id}: Mk I modules have no recipe` })
      }
    })
    for (const id of MODULE_IDS) {
      if (!seen.has(id)) ctx.addIssue({ code: 'custom', path: ['modules'], message: `module ${id} is missing (every kind needs Mk I-III exactly once)` })
    }
    for (const kind of MODULE_KINDS) {
      let last = 0
      for (const mk of MODULE_MKS) {
        const i = seen.get(moduleId(kind, mk))
        if (i === undefined) continue
        const price = f.modules[i].price
        if (price <= last) ctx.addIssue({ code: 'custom', path: ['modules', i, 'price'], message: `${moduleId(kind, mk)} must cost more than the Mk below it` })
        last = price
      }
      const slots = Object.values(f.slots).filter((kinds) => kinds.includes(kind)).length
      if (slots === 0) ctx.addIssue({ code: 'custom', path: ['slots'], message: `no slot accepts module kind "${kind}"` })
    }
    for (const slot of SLOT_IDS) uniqueIssues(f.slots[slot] ?? [], ctx, ['slots', slot], 'module kind')
  })

export type ModuleRaw = z.infer<typeof moduleRawSchema>
export type ModulesFileRaw = z.infer<typeof modulesFileSchema>

/** A module with typed stats, discriminated by `kind`. */
export type ModuleDef = {
  [K in ModuleKind]: {
    id: ModuleId
    kind: K
    mk: ModuleMk
    /** A pattern module's pattern (M8 MC-f2a; set by `resolveModule`); absent on base modules. */
    pattern?: PatternId
    name: string
    price: number
    power: number
    stats: ModuleStatsByKind[K]
    /** Mk II/III recipe as in the file (M8 MC-c1a); read it through `craft/recipes.ts recipeFor`. */
    materials?: FabMaterialsRaw
  }
}[ModuleKind]

export interface ModulesData {
  slots: Record<SlotId, ModuleKind[]>
  sellFraction: number
  /** M8 MC-c7's scrap share (absent until it lands). */
  scrapFraction?: number
  /** M8 fabrication settings (MC-c1a). */
  materials?: ModulesMaterials
  weapons: { pdc: WeaponId; laser: WeaponId }
  /** Index of each upgraded M1 weapon in `GameData.weapons.weapons`. */
  weaponIndex: { pdc: number; laser: number }
  /** In `MODULE_IDS` order. */
  modules: ModuleDef[]
  /**
   * The base modules by id; pattern ids are not in it. Read modules through `data/m2.ts resolveModule` (or
   * `moduleDef`/`moduleStats`), never this map: only `data/m2.ts` reads it (MC-f2b; `moduleReaders.test.ts` greps for that).
   */
  byId: Record<BaseModuleId, ModuleDef>
  /** M6 utility modules (escape pod, heavy tractor); bundled data always carries them. */
  utility?: UtilityModules
}

// ---------------------------------------------------------------------------------------------
// mining.json (spec §5)
// ---------------------------------------------------------------------------------------------

/** Relative ore weights per archetype (tetherstone comes only from the `tetherstone` block). */
const oreWeightsSchema = z.partialRecord(wareIdSchema, nonNegative)

export const miningFileSchema = z
  .strictObject({
    /** Rock mass = this × radius³ tonnes (0.2: rocks from about 12.6 m reach 400 t). */
    massPerCubicMetre: positive,
    /** FRACTURE when mass ≥ this (t) ... */
    fractureMassT: positive,
    /** ... or estimated value ≥ this (credits); otherwise CHIP. */
    fractureValue: positive,
    /** Ore weights per archetype A-F (docs/art/sheets/asteroid-set.md). */
    archetypeOres: z.strictObject({ A: oreWeightsSchema, B: oreWeightsSchema, C: oreWeightsSchema, D: oreWeightsSchema, E: oreWeightsSchema, F: oreWeightsSchema }),
    /**
     * Per-rock seeded jitter on the weights (×(1 ± jitter)) and the most ores one rock carries. `fieldBlend` lerps the
     * archetype weights toward the galaxy field's mix (0 = archetype only, today); `gradeMassMult` scales a rock's ore
     * mass by its field's grade multiplier (MC-a5b; both off until MC-a6).
     */
    mix: z.strictObject({
      jitter: unit,
      maxOres: int.min(1).max(WARE_COUNT),
      fieldBlend: unit.default(0),
      gradeMassMult: z.boolean().default(false),
    }),
    /** Chance a FRACTURE rock carries tetherstone, and its share of the mix. */
    tetherstone: z.strictObject({ chance: unit, share: minMax(unit) }),
    beam: z.strictObject({ range: positive, socket: hpSocket }),
    /**
     * Mining booms (M10 P5a12b): a ship def that lists `mining.booms` fires one beam per boom socket on this one CHIP /
     * collector / FRACTURE model, each with its own target, chip timer and laser heat. Defs without booms use `beam`
     * above, unchanged. `rangeM`: each boom's reach (m; replaces `beam.range` on that hull). `arcDeg`: a boom's full
     * aiming arc about the nose, read by the boom targeting (P5a12c). `heatShare`: each boom's heat rate × one laser's.
     */
    booms: z.strictObject({ rangeM: positive, arcDeg: z.number().gt(0).max(180), heatShare: positive }),
    scan: z.strictObject({ holdSeconds: positive, valueSpreadMk1: unit, valueSpreadMk2: unit }),
    chip: z.strictObject({
      /** Seconds of beam contact per chunk at chip rate ×1. */
      intervalSeconds: positive,
      chunkMassT: minMax(positive),
      /** Chunk speed off the surface, m/s. */
      ejectSpeed: minMax(nonNegative),
      /** Fragments of a fractured rock chip this much more ore per chunk. */
      fragmentYieldMult: positive,
      chunkLifeSeconds: positive,
      /** Chunk pool size (fixed, D-014). */
      poolCapacity: int.min(1),
      /** Laser power used while chipping (drives heat). */
      chipPower: unit,
    }),
    collector: z.strictObject({
      range: positive,
      halfAngleDeg: z.number().gt(0).max(90),
      pullAccel: positive,
      maxPullSpeed: positive,
      captureDistance: positive,
    }),
    fracture: z.strictObject({
      /** Resistance tiers: tier = floor(resistance × tiers). */
      tiers: int.min(1),
      /** Stability-window width at resistance 0 and 1 (fractions of the power bar). */
      windowWidth: lerpPair(unit),
      /** Peak window speed at resistance 0 and 1 (power-bar fractions per second). */
      windowSpeed: minMax(nonNegative),
      /** Seconds inside the window to fracture. */
      fractureSeconds: positive,
      /** Below the window, progress decays at this × the fill rate. */
      belowDecay: nonNegative,
      /** Above the window, instability rises at this × overshoot per second. */
      instabilityPerOvershoot: positive,
      /** Instability decay per second inside the window. */
      instabilityDecay: nonNegative,
      /** One wheel or D-pad step of laser power. */
      powerStep: z.number().gt(0).max(1),
      /** Exponential smoothing rate of power toward its target, 1/s. */
      powerSmoothing: positive,
      /** Seconds off the rock before the fracture disengages. */
      disengageSeconds: nonNegative,
      fragments: minMax(int.min(1)),
      /** Fragment scatter as a fraction of the parent radius. */
      fragmentSpread: positive,
      /** Fragment pool size (fixed, D-014). */
      fragmentPoolCapacity: int.min(1),
      burst: z.strictObject({
        /** Damage at the rock and at `radius` (lerped by distance). */
        damage: minMax(nonNegative),
        radius: positive,
        /** Share of the rock's ore lost. */
        oreLoss: unit,
        chunks: minMax(int.min(0)),
        maxChunkTonnes: positive,
      }),
    }),
    heat: z.strictObject({ ratePerPower: positive, coolRate: positive, overheatCooldownSeconds: positive }),
  })
  .superRefine((f, ctx) => {
    for (const [arch, weights] of Object.entries(f.archetypeOres)) {
      let sum = 0
      for (const v of Object.values(weights)) sum += v ?? 0
      if (!(sum > 0)) ctx.addIssue({ code: 'custom', path: ['archetypeOres', arch], message: `archetype ${arch}: ore weights must sum to more than 0` })
      if (weights.tetherstone !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['archetypeOres', arch, 'tetherstone'], message: 'tetherstone comes only from the tetherstone block (fracture rocks)' })
      }
      // 5(e) (MC-e2): an archetype mix is used everywhere, the Gate Cluster included, so the deep and rim ore is in none.
      // It reaches rocks only through a galaxy field's mix (`mix.fieldBlend`, galaxy.json `minerals`).
      if (weights.refractory_ore !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['archetypeOres', arch, 'refractory_ore'], message: 'refractory_ore comes only from deep and rim galaxy fields (mix.fieldBlend)' })
      }
    }
  })

export type MiningFileRaw = z.infer<typeof miningFileSchema>

/** Runtime mining config: the collector cone in radians, archetype weights as dense arrays. */
export interface MiningConfig extends Omit<MiningFileRaw, 'collector'> {
  collector: Omit<MiningFileRaw['collector'], 'halfAngleDeg'> & { /** Radians. */ halfAngle: number }
  /** [archetype index 0-5 = A-F][ware index] → weight (tetherstone always 0). Hot-loop friendly. */
  archetypeWeights: number[][]
}

// ---------------------------------------------------------------------------------------------
// salvage.json (spec §6)
// ---------------------------------------------------------------------------------------------

/** Where a layout slot sits: a named socket of the ship model, or a point across its bounds box (−1..1 per axis). */
export const slotAnchorSchema = z.union([
  z.strictObject({ socket: z.string().regex(SOCKET_NAME) }),
  z.strictObject({ bounds: z.tuple([signedUnit, signedUnit, signedUnit]) }),
])

export const layoutSlotSchema = z.strictObject({ type: componentTypeSchema, at: slotAnchorSchema })

export const derelictPartSchema = z.strictObject({
  type: componentTypeSchema,
  /** Metres from the section centre, derelict-local frame (+Y spindle, −Z toward the spawn). */
  offset: vec3Tuple,
  /** Fixed condition (0..1); omitted = seeded in `wrecks.condition`. */
  condition: unit.optional(),
})

export const derelictSectionSchema = z.strictObject({
  id: slug,
  /** Derelict-local metres (on a module of the assembled Harrow, src/render/environment/derelict.ts). */
  center: vec3Tuple,
  parts: z.array(derelictPartSchema).min(1).max(MAX_PARTS_PER_WRECK),
})

/** [Mk I, Mk II, Mk III] shares of a region (`salvage.json tiers.byRegion`). */
const mkShares = z.tuple([unit, unit, unit])
/** Rounding slack when Mk shares are checked to sum to 1. */
const MK_SHARE_EPSILON = 1e-6

export const salvageFileSchema = z
  .strictObject({
    beam: z.strictObject({
      range: positive,
      socket: hpSocket,
      /** The beam snaps to the uncut anchor nearest the reticle within this angle. */
      assistDeg: positive,
      /** Beam contact within this distance of an anchor counts as on it (m). */
      anchorRadius: positive,
      /** Contact within this distance but off an uncut anchor spills heat into the part (m). */
      spillRadius: positive,
      /** Per-anchor cut time before the beam Mk multiplier. */
      cutSeconds: minMax(positive),
    }),
    tractor: z.strictObject({
      coneDeg: z.number().gt(0).max(90),
      pullAccel: positive,
      maxSpeed: positive,
      captureDistance: positive,
      /** Ship-local point the tractor pulls parts toward (m). */
      holdPoint: vec3Tuple,
    }),
    coolant: z.strictObject({ damage: nonNegative, radius: positive }),
    /**
     * Condition bands (economy plan SY-1): Intact at `intact` or above, Serviceable from `serviceable` up to below
     * `intact`, Wrecked below `serviceable`. Derived from a part's condition, never stored. M7's reactor gate reads
     * `serviceable`.
     */
    bands: z.strictObject({ intact: unit, serviceable: unit }),
    wrecks: z.strictObject({
      combatLifeSeconds: positive,
      lootedLifeSeconds: positive,
      /** Linear drag on drifting wrecks, 1/s. */
      drag: nonNegative,
      /** Chance each layout slot yields a part. */
      partChance: unit,
      condition: minMax(unit),
      maxWrecks: int.min(1),
      maxParts: int.min(1),
      /** Extra outward speed of a freed part (m/s). */
      freedPush: nonNegative,
    }),
    /** Salvage layouts per ship id (`ships.json`). */
    layouts: z.record(z.string().min(1), z.array(layoutSlotSchema).min(1).max(MAX_PARTS_PER_WRECK)),
    /** The player's death wreck: a one-time raider loot check. */
    deathWreck: z.strictObject({ lootCheckSeconds: positive, lootChance: unit }),
    /** The First Rush derelict (the Harrow): 3-5 salvageable sections. */
    derelict: z.strictObject({ sections: z.array(derelictSectionSchema).min(3).max(5) }),
    /**
     * Salvaged component Mk (economy plan SY-4a; placeholders for the balance pass). `byRegion`: the [Mk I, Mk II,
     * Mk III] shares a Mk-carrying part rolls in each region (they sum to 1). `maxIntactMk3`: the crafting guard
     * (R3-M7), the most of all rolled parts that may be both Intact and Mk III in each region (rim highest); a data
     * test holds the rolled shares to it.
     */
    tiers: z.strictObject({
      byRegion: z.strictObject({ gate: mkShares, mid: mkShares, deep: mkShares, rim: mkShares }),
      maxIntactMk3: z.strictObject({ gate: unit, mid: unit, deep: unit, rim: unit }),
    }),
    /**
     * Rush-pattern provenance (M8 MC-h1, D-TBD T38; placeholders for the balance pass): the share of a derelict's parts
     * tagged Rush-pattern in each region (gate always 0, more the deeper), × `graveyardMult` in a derelict-graveyard
     * system. Never on sealed or story sites; no budget and no decay (decision 13: the derelicts are the limit).
     */
    rush: z.strictObject({
      byRegion: z.strictObject({ gate: z.literal(0), mid: unit, deep: unit, rim: unit }),
      graveyardMult: z.number().min(1),
    }),
    /**
     * Graveyard salvage sites (M8 MC-h2; placeholders for the balance pass): in a derelict-graveyard system up to
     * `extraSites` more of its derelict POIs are cuttable besides the salvage site (`bridge/m2/systemWorld.ts
     * graveyardSalvageSites`). Each sits at least `minSpacingM` from every other cuttable derelict, `storyClearM`
     * beyond a story site's radius, and `dockClearM` from a restorable derelict (its docking volume once M7 restarts
     * it). The wreck and part pools hold them all (a data test).
     */
    graveyardSites: z.strictObject({
      extraSites: int.min(0).max(4),
      minSpacingM: positive,
      storyClearM: positive,
      dockClearM: positive,
    }),
    /**
     * Recondition bills (economy plan SY-5b, D8: paid in goods only, no credit fee): per component type, the goods
     * (`career/parts.ts GOOD_IDS`, units per whole condition point) a Serviceable component takes to reach
     * `bands.intact`, scaled by `bands.intact − condition`. A type without a bill is not reconditioned. The good ids
     * are checked against the goods list by `career/recondition.ts reconditionTableSchema` (a data test), since the
     * goods list lives on the career side. Placeholders; R3-M3's money-press data test bounds them from below.
     */
    recondition: z.partialRecord(componentTypeSchema, z.strictObject({ goods: z.record(z.string().regex(/^[a-z][a-z0-9_]*$/), positive) })),
    /**
     * Data-core delivery (economy plan SY-7, D-TBD 12; placeholders for the balance pass): a generic data core in the
     * hold can be handed to `faction`'s market stations for standing, no credits (`career/cores.ts`). Each core gives
     * `standing × perRegion[region]` (rounded) with that faction, where `region` is the galaxy region of the system
     * the delivery is made in (×1 when the region is unknown, e.g. M2 without the Reach).
     */
    coreDelivery: z.strictObject({
      faction: simFactionIdSchema,
      standing: positive,
      perRegion: z.strictObject({ gate: positive, mid: positive, deep: positive, rim: positive }),
    }),
    /**
     * Local hulks (M9 CH-3a, D-TBD M9-4): disabled, claimable ships in the cockpit (`WRECK_KIND.hulk`, no parts and no
     * timer). `maxLocal`: the most unclaimed ones live at once in a system (seeded and off-screen ones together).
     * `maxClaimed` (PG-2 R2-4): the most live that hold an open claim, outside `maxLocal`; at least `fleet.json
     * claim.maxOpen`, so every open claim's hulk always spawns. The wreck pool holds `maxLocal + maxClaimed` beside
     * every derelict section with the old room to spare (data tests).
     */
    hulks: z.strictObject({ maxLocal: int.min(0).max(8), maxClaimed: int.min(0).max(8) }),
  })
  .superRefine((f, ctx) => {
    if (f.beam.spillRadius <= f.beam.anchorRadius) {
      ctx.addIssue({ code: 'custom', path: ['beam', 'spillRadius'], message: 'spillRadius must exceed anchorRadius' })
    }
    if (f.bands.serviceable >= f.bands.intact) {
      ctx.addIssue({ code: 'custom', path: ['bands', 'serviceable'], message: 'bands.serviceable must be below bands.intact' })
    }
    uniqueIssues(
      f.derelict.sections.map((s) => s.id),
      ctx,
      ['derelict', 'sections'],
      'derelict section id',
    )
    for (const region of SALVAGE_REGIONS) {
      const [p1, p2, p3] = f.tiers.byRegion[region]
      if (Math.abs(p1 + p2 + p3 - 1) > MK_SHARE_EPSILON) {
        ctx.addIssue({ code: 'custom', path: ['tiers', 'byRegion', region], message: 'Mk I-III shares must sum to 1' })
      }
      if (region !== 'rim' && f.tiers.maxIntactMk3[region] > f.tiers.maxIntactMk3.rim) {
        ctx.addIssue({ code: 'custom', path: ['tiers', 'maxIntactMk3', region], message: 'the rim has the highest Intact Mk III bound' })
      }
    }
    for (const region of SALVAGE_REGIONS) {
      if (f.rush.byRegion[region] * f.rush.graveyardMult > 1) {
        ctx.addIssue({ code: 'custom', path: ['rush', 'graveyardMult'], message: `the ${region} Rush share × graveyardMult exceeds 1` })
      }
    }
    const types = f.derelict.sections.flatMap((s) => s.parts.map((p) => p.type))
    if (!types.includes('reactor')) ctx.addIssue({ code: 'custom', path: ['derelict'], message: 'the derelict needs at least one reactor (a coolant hazard)' })
    if (!types.includes('dataCore')) ctx.addIssue({ code: 'custom', path: ['derelict'], message: 'the derelict needs at least one data core' })
  })

export type SlotAnchor = z.infer<typeof slotAnchorSchema>
export type LayoutSlot = z.infer<typeof layoutSlotSchema>
export type DerelictSection = z.infer<typeof derelictSectionSchema>
export type SalvageFileRaw = z.infer<typeof salvageFileSchema>

/** Runtime salvage config: angles in radians, layouts also indexed by ship def index. */
export interface SalvageConfig extends Omit<SalvageFileRaw, 'beam' | 'tractor'> {
  beam: Omit<SalvageFileRaw['beam'], 'assistDeg'> & { /** Radians. */ assist: number }
  tractor: Omit<SalvageFileRaw['tractor'], 'coneDeg'> & { /** Radians (half-angle). */ cone: number }
  /** `layouts` by `GameData.ships` index (null = the ship leaves no salvage). */
  layoutByDef: (LayoutSlot[] | null)[]
}

// ---------------------------------------------------------------------------------------------
// docking.json (spec §7.2)
// ---------------------------------------------------------------------------------------------

export const dockingFileSchema = z
  .strictObject({
    comment: z.string().optional(),
    /** From the station's hull: centre distance less the station radius (m; scale build §3.3). */
    requestRange: positive,
    clearanceSeconds: positive,
    /** From the station's hull, like `requestRange` (m). */
    autodockRange: positive,
    corridor: z.strictObject({ length: positive, ringSpacing: positive, radiusMult: positive }),
    speedLimits: z.strictObject({ corridor: positive, bay: positive }),
    /** Dock completes inside the bay under maxSpeed with nose and roll errors ≤ maxAlignDeg, held holdSeconds. */
    dock: z.strictObject({ maxSpeed: positive, maxAlignDeg: z.number().gt(0).max(90), holdSeconds: positive }),
    /** Hull damage = max(0, impactSpeed − freeSpeed) × damagePerMps for the player within proximity of a station. */
    impact: z.strictObject({ freeSpeed: nonNegative, damagePerMps: nonNegative, proximity: positive }),
    launch: z.strictObject({ speed: positive }),
    autopilot: z.strictObject({
      entryDistance: positive,
      approachSpeed: positive,
      /** Detour and entry legs beyond `detourNear` of the entry point fly at up to this (m/s; scale build SC-5a1b). */
      detourSpeed: positive,
      /** Within this distance of the entry point (m) the detour and entry legs fly at `approachSpeed`. */
      detourNear: positive,
      corridorSpeed: positive,
      baySpeed: positive,
      finalSpeed: positive,
      lateralGain: positive,
      turnGain: positive,
      rollGain: positive,
      stationMargin: positive,
      arriveRadius: positive,
      /** The clearance (m) the hull keeps to each mouth wall when the autopilot lets it cross the mouth (D-TBD). */
      hullMargin: nonNegative,
    }),
  })
  .superRefine((f, ctx) => {
    const order = (path: string[], ok: boolean, message: string): void => {
      if (!ok) ctx.addIssue({ code: 'custom', path, message })
    }
    const a = f.autopilot
    order(['autodockRange'], f.autodockRange <= f.requestRange, 'autodockRange must not exceed requestRange')
    order(['speedLimits', 'bay'], f.speedLimits.bay <= f.speedLimits.corridor, 'the bay limit must not exceed the corridor limit')
    order(['dock', 'maxSpeed'], f.dock.maxSpeed <= f.speedLimits.bay, 'dock.maxSpeed must not exceed the bay limit')
    order(['autopilot', 'finalSpeed'], a.finalSpeed < f.dock.maxSpeed, 'autopilot.finalSpeed must be below dock.maxSpeed')
    order(['autopilot', 'baySpeed'], a.finalSpeed <= a.baySpeed && a.baySpeed <= f.speedLimits.bay, 'autopilot.baySpeed must lie in [finalSpeed, the bay limit]')
    order(
      ['autopilot', 'corridorSpeed'],
      a.baySpeed <= a.corridorSpeed && a.corridorSpeed <= f.speedLimits.corridor,
      'autopilot.corridorSpeed must lie in [baySpeed, the corridor limit]',
    )
    order(['autopilot', 'approachSpeed'], a.corridorSpeed <= a.approachSpeed, 'autopilot.approachSpeed must not be below corridorSpeed')
    order(['autopilot', 'detourSpeed'], a.approachSpeed <= a.detourSpeed, 'autopilot.detourSpeed must not be below approachSpeed')
  })

export type DockingFileRaw = z.infer<typeof dockingFileSchema>

/** Runtime docking config: `dock.maxAlign` in radians. */
export interface DockingConfig extends Omit<DockingFileRaw, 'dock'> {
  dock: Omit<DockingFileRaw['dock'], 'maxAlignDeg'> & { /** Radians (nose and roll error). */ maxAlign: number }
}

// ---------------------------------------------------------------------------------------------
// lease.json (spec §9.2)
// ---------------------------------------------------------------------------------------------

export const leaseFileSchema = z
  .strictObject({
    principal: positive,
    payment: positive,
    /** Game seconds (in-space time) per lease cycle. */
    cycleSeconds: positive,
    /** Interest capitalised on the principal at each due time. */
    ratePerCycle: unit,
    lateFee: nonNegative,
    /** Rate increase per missed payment (permanent until ownership), capped at rateCap. */
    rateStepOnMiss: nonNegative,
    rateCap: unit,
    /** Missed payments that turn collections to `repo`. */
    repoAfterMissed: int.min(1),
    repoFee: nonNegative,
    /** Death rebuild fee = base + moduleValueShare × fitted module value. */
    rebuild: z.strictObject({ base: nonNegative, moduleValueShare: unit }),
    /** Optional after ownership: a premium per cycle halves (× insuredFeeMult) the rebuild fee. */
    insurance: z.strictObject({ premiumPerCycle: nonNegative, insuredFeeMult: unit }),
    /** Post-ownership insurer debt repaid per cycle (interest-free). */
    debtPaymentPerCycle: positive,
    /** Lease history keeps the last this-many entries. */
    historyLimit: int.min(1),
    /**
     * M7 S33 seeds (spec §12.4; text only): the Tethys Crew Finance statement and its signature (PC-8), the Lessee
     * Conduct Covenant (recall for "reputational harm", the extraction clause) and Duarte's reading at signing, and the
     * repo crew (PC-6: Tethys Asset Recovery, its livery tag and marks). Absent = the Lease Office shows none.
     */
    office: z
      .strictObject({
        statement: z.strictObject({ issuer: name, signedBy: seedPersonSchema }),
        covenant: z.strictObject({
          title: name,
          clauses: z.array(name).min(1),
          /** Who reads it aloud at signing, and the words (hour one's live line). */
          reading: seedLineSchema,
        }),
        recovery: z.strictObject({
          crew: name,
          /** The livery tag stencilled on the repo crew's hulls. */
          liveryTag: name,
          marks: z.array(decalMarkSchema),
        }),
      })
      .optional(),
  })
  .superRefine((f, ctx) => {
    if (f.ratePerCycle > f.rateCap) ctx.addIssue({ code: 'custom', path: ['ratePerCycle'], message: 'ratePerCycle must not exceed rateCap' })
  })

export type LeaseConfig = z.infer<typeof leaseFileSchema>

// ---------------------------------------------------------------------------------------------
// contracts.json (spec §9.3)
// ---------------------------------------------------------------------------------------------

export const contractsFileSchema = z
  .strictObject({
    comment: z.string().optional(),
    /** Offers per station per lease cycle. */
    perStation: minMax(int.min(0)),
    /** Standing that unlocks the higher-paying variants (× unlockMult). */
    unlockStanding: standing,
    unlockMult: z.number().min(1),
    /** Standing gained with the issuing faction on completion. */
    standingReward: minMax(nonNegative),
    /** Standing lost on failure or abandonment. */
    standingPenalty: nonNegative,
    /**
     * Per-faction override of `standingReward` (D-335 D4, M7 spec §12.5): while the player's standing with the
     * faction is below `until`, its contracts pay a flat `flat`; from `until` on, the seeded `standingReward` range.
     */
    factionStanding: z.partialRecord(simFactionIdSchema, z.strictObject({ flat: nonNegative, until: standing })),
    /**
     * The comms-only reconciliation offer (D-335 D4): at standing ≤ `atOrBelow` with `faction` (its docking
     * refusal), paying `credits` over comms sets the standing to `restoreTo`. Repeatable; needs no docking.
     */
    reconciliation: z.strictObject({ faction: simFactionIdSchema, atOrBelow: standing, restoreTo: standing, credits: positive }),
    /**
     * M7 S12 (spec §5, §16.2): a contraband story item found by a patrol scan costs `contrabandFine` credits and is
     * never confiscated; at Authority standing ≥ `waveThroughStanding` the patrol waves the player through (Hale's
     * office logs the route). The nth Holdfast contract handed in drops the named Watch log (the 1st and 3rd). The
     * Keep the Candle lit run delivers `goods` to the Harrow (pay: story reactivation.json candleRun).
     */
    story: z
      .strictObject({
        contrabandFine: nonNegative,
        waveThroughStanding: standing,
        holdfastWatchLogs: z.array(z.strictObject({ nth: int.min(1), log: z.string().min(1) })),
        candleRun: z.strictObject({
          target: z.string().min(1),
          goods: z.array(z.strictObject({ good: z.string().min(1), qty: int.min(1) })).min(1),
        }),
      })
      .optional(),
    types: z.strictObject({
      delivery: z.strictObject({ weight: nonNegative, tons: minMax(positive), rewardMult: positive, deadlineCycles: int.min(1) }),
      /** `markerDistance` [min, max] (m) is from the issuing station's hull (R + d from its centre; scale SC-5e1). */
      recovery: z.strictObject({ weight: nonNegative, rewardBonus: nonNegative, deadlineCycles: int.min(1), markerDistance: minMax(positive) }),
      clearRaiders: z.strictObject({
        weight: nonNegative,
        reward: minMax(positive),
        deadlineCycles: int.min(1),
        /** From the issuing station's hull (R + d from its centre; scale SC-5e1). */
        markerDistance: minMax(positive),
        /** The group spawns when the player comes within this distance of the marker. */
        spawnWithin: positive,
      }),
      courier: z.strictObject({ weight: nonNegative, reward: minMax(positive), deadlineSeconds: positive, podMassT: positive }),
      // M3 bridge (R22g): issued only in the Reach. Rewards derive from the relay parts' stand-in prices.
      /** Repair a down lane ring in the station's system: reward = the ring's repair parts' value × rewardMult. */
      laneRepair: z.strictObject({ weight: nonNegative, rewardMult: positive, deadlineCycles: int.min(1) }),
      /** Deliver N parts (drawn from `units`, at most what is missing) to an offline relay: reward = N × part value × rewardMult. */
      relayParts: z.strictObject({ weight: nonNegative, units: minMax(int.min(1)), rewardMult: positive, deadlineCycles: int.min(1) }),
      /** Chart a system 1..maxHops relay hops away: reward × (1 + hopBonus × (hops − 1)). */
      chartSystem: z.strictObject({
        weight: nonNegative,
        reward: minMax(positive),
        hopBonus: nonNegative,
        maxHops: int.min(1),
        deadlineCycles: int.min(1),
      }),
    }),
  })
  .superRefine((f, ctx) => {
    const t = f.types
    if (!(t.delivery.weight + t.recovery.weight + t.clearRaiders.weight + t.courier.weight > 0)) {
      ctx.addIssue({ code: 'custom', path: ['types'], message: 'contract type weights must sum to more than 0' })
    }
    if (!(f.reconciliation.restoreTo > f.reconciliation.atOrBelow)) {
      ctx.addIssue({ code: 'custom', path: ['reconciliation', 'restoreTo'], message: 'reconciliation.restoreTo must be above atOrBelow' })
    }
  })

export type ContractsConfig = z.infer<typeof contractsFileSchema>

// ---------------------------------------------------------------------------------------------
// encounters.json (spec §9.2-§9.3)
// ---------------------------------------------------------------------------------------------

export const encountersFileSchema = z
  .strictObject({
    comment: z.string().optional(),
    /** Game seconds between director rolls [min, max]. */
    intervalSeconds: minMax(positive),
    /** Roll chance = min(maxChance, baseChance + cargoValue / cargoValueScale). */
    baseChance: unit,
    cargoValueScale: positive,
    maxChance: unit,
    /** Raider group size by cargo value: the highest tier whose minCargoValue ≤ the cargo value (M1 wave numbers). */
    tiers: z.array(z.strictObject({ minCargoValue: nonNegative, wave: int.min(1) })).min(1),
    /** Spawn distance from the player [min, max] (m). */
    spawnDistance: minMax(positive),
    /**
     * Never spawn, and no contract marker, within this distance of a station's hull (m; R + this from its centre, R the
     * bounding radius; scale SC-5e1). The director also holds its rolls while the player is this close to a hull.
     */
    stationExclusion: positive,
    /** Groups this far from the player for long enough despawn quietly (m). */
    despawnDistance: positive,
    /** Most encounter groups alive at once (fixed pool, D-014). */
    maxGroups: int.min(1),
    repo: z.strictObject({
      ships: z.array(z.strictObject({ ship: name, profile: name })).min(1),
      /** The repo crew arrives this long after an undock (game seconds). */
      undockDelaySeconds: nonNegative,
      /** ... or within this long when collections turn `repo` in space. */
      inSpaceWithinSeconds: positive,
    }),
    /** Clear-raiders contract group per contract tier (M1 wave numbers). */
    contractTiers: z.array(int.min(1)).min(1),
    /** M10 P4b6a: a raid on a listed band draws its wave tierStep cargo tiers higher; the first pack ship `kit` fits flies it. */
    bandScale: z.partialRecord(shipBandSchema, z.strictObject({ tierStep: int.min(1), kit: name })).optional(),
  })
  .superRefine((f, ctx) => {
    if (f.tiers[0].minCargoValue !== 0) ctx.addIssue({ code: 'custom', path: ['tiers', 0, 'minCargoValue'], message: 'the first tier must start at cargo value 0' })
    for (let i = 1; i < f.tiers.length; i++) {
      if (f.tiers[i].minCargoValue <= f.tiers[i - 1].minCargoValue) {
        ctx.addIssue({ code: 'custom', path: ['tiers', i, 'minCargoValue'], message: 'tiers must increase in minCargoValue' })
      }
    }
    if (f.baseChance > f.maxChance) ctx.addIssue({ code: 'custom', path: ['baseChance'], message: 'baseChance must not exceed maxChance' })
  })

export type EncountersFileRaw = z.infer<typeof encountersFileSchema>

/** A ship reference resolved against `GameData` (like the M1 `WaveShip`). */
export interface ResolvedShipRef {
  ship: string
  profile: string
  /** Index into `GameData.ships`. */
  defIndex: number
  /** Index into `GameData.aiProfiles.profiles`. */
  profileIndex: number
}

/** Runtime encounters config with every reference resolved. */
export interface EncountersConfig extends Omit<EncountersFileRaw, 'tiers' | 'repo'> {
  /** `wave` is 1-based as in waves.json; `waveIndex` indexes `GameData.waves.waves`. */
  tiers: { minCargoValue: number; wave: number; waveIndex: number }[]
  repo: Omit<EncountersFileRaw['repo'], 'ships'> & { ships: ResolvedShipRef[] }
  /** `contractTiers` as indices into `GameData.waves.waves`. */
  contractWaveIndex: number[]
}

// ---------------------------------------------------------------------------------------------
// career.json (spec §3.2, §9.2, §10)
// ---------------------------------------------------------------------------------------------

/**
 * The career file's `berth` defaults (MC-b4); `career.json berth` overrides them. The caps are the M8 plan's 64 / 512,
 * the relocation delay is a placeholder until MC-b5 sets it, and remote pull is U8's placeholder (2 jumps; Cargo
 * Mk III or standing 25 with the Mutual) until MC-b7 sets it.
 */
const BERTH_DEFAULTS = {
  componentCapPerStation: 64,
  componentCapTotal: 512,
  relocateDelayDays: 3,
  remotePull: { jumps: 2, unlock: { module: 'cargo.mk3' as ModuleId, faction: 'mutual' as SimFactionId, standing: 25 } },
}

export const careerFileSchema = z.strictObject({
  start: z.strictObject({
    wallet: nonNegative,
    /** Station the career starts docked at. */
    station: slug,
    /** Ship id (`ships.json`). */
    ship: name,
    /** The stock fit (all Mk I). */
    fitted: z.record(slotIdSchema, moduleIdSchema),
    standings: z.record(simFactionIdSchema, standing),
  }),
  /** Credits per hull point repaired. */
  repairPerHull: positive,
  torpedoPrice: positive,
  /**
   * The goods trade switch (economy plan §2.2 goods deploy rule, R3-H2). While false, goods can't be bought (except
   * the relay parts), sold or broken down. It flips on only in the deploy that carries SY-2b and SY-2d.
   */
  goods: z.strictObject({ tradeEnabled: z.boolean() }),
  /**
   * M8 (MC-b4 writes the whole block; MC-b5 and MC-b7 only set values): the berth store (`career/berth.ts`). Free: no
   * rent, fee or tonnage cap. Only stored components are capped, per station and over every berth (the save-size
   * bound). Absent = the defaults, so a career file from before MC-b4 parses.
   */
  berth: z
    .strictObject({
      /** Components one berth holds. */
      componentCapPerStation: int.min(1),
      /** Components every berth holds together. */
      componentCapTotal: int.min(1),
      /** MC-b5 (T21): game days after access is lost before a berth relocates for free. */
      relocateDelayDays: nonNegative.default(BERTH_DEFAULTS.relocateDelayDays),
      /**
       * MC-b7 (T22, U8): fab orders draw from berths within `jumps` relay jumps once unlocked, by fitting
       * `unlock.module` (or better) or with `unlock.standing` standing with `unlock.faction`.
       */
      remotePull: z
        .strictObject({
          jumps: int.min(0),
          unlock: z.strictObject({ module: moduleIdSchema, faction: simFactionIdSchema, standing }),
        })
        .default(BERTH_DEFAULTS.remotePull),
    })
    .default(BERTH_DEFAULTS),
  /**
   * M8, reserved here (MC-b4 writes the schema; MC-h3 sets `base`, MC-h4a `premium` and `standingGain`): First Rush
   * spares, the career-only good. `base` is credits per unit (pod and hold value); a Holdfast sale pays
   * `base × premium` per unit and gains `standingGain` standing. Absent = no Rush spares yet.
   */
  rushSpares: z
    .strictObject({
      base: positive,
      premium: positive.default(1),
      standingGain: nonNegative.default(0),
    })
    .optional(),
  /** UI snapshot rate (Hz). */
  snapshotHz: positive,
  /** Sim seconds between death and the rebuild. */
  deathRespawnSeconds: nonNegative,
  save: z.strictObject({
    manualSlots: int.min(1).max(3),
    autoSlots: int.min(1).max(3),
    /** Autosave interval in space (game seconds). */
    autosaveSeconds: positive,
    /** Size target per save (spec §10); larger saves still save and log a warning. */
    maxBytes: int.min(1),
    dbName: name,
    store: name,
    localStorageKey: name,
  }),
  /** M7 S33 seed (PC-1): the start ship's frame and serial, and its registration, on the ship screen. */
  ship: z.strictObject({ frame: name, serial: name, registration: name }).optional(),
})

export type CareerFileRaw = z.infer<typeof careerFileSchema>

/** Runtime career config: the start ship resolved against `GameData`. */
export interface CareerConfig extends Omit<CareerFileRaw, 'start'> {
  start: CareerFileRaw['start'] & {
    /** Index of `start.ship` in `GameData.ships`. */
    shipDefIndex: number
    /** Full hull of the start ship (the def's hull; no module changes hull). */
    hull: number
    /** Torpedo rounds of the start ship. */
    torpedoes: number
  }
}

// ---------------------------------------------------------------------------------------------
// All M2 data
// ---------------------------------------------------------------------------------------------

/** The ten M2 JSON files as parsed (the `m2DataToRaw` output shape). */
export interface M2RawFiles {
  wares: WaresFileRaw
  stations: StationsFileRaw
  modules: ModulesFileRaw
  mining: MiningFileRaw
  salvage: SalvageFileRaw
  docking: DockingFileRaw
  lease: LeaseConfig
  contracts: ContractsConfig
  encounters: EncountersFileRaw
  career: CareerFileRaw
}

export type M2FileKey = keyof M2RawFiles

/** File name of each M2 data file (error messages name them). */
export const M2_FILES: Record<M2FileKey, string> = {
  wares: 'wares.json',
  stations: 'stations.json',
  modules: 'modules.json',
  mining: 'mining.json',
  salvage: 'salvage.json',
  docking: 'docking.json',
  lease: 'lease.json',
  contracts: 'contracts.json',
  encounters: 'encounters.json',
  career: 'career.json',
}

/** Every parsed, converted and cross-validated M2 file (`loadM2Data`). Mutable for live tuning. */
export interface M2Data {
  wares: WaresData
  stations: StationsData
  modules: ModulesData
  mining: MiningConfig
  salvage: SalvageConfig
  docking: DockingConfig
  lease: LeaseConfig
  contracts: ContractsConfig
  encounters: EncountersConfig
  career: CareerConfig
}
